import { autoinject, BindingEngine, computedFrom, transient } from "aurelia-framework";
import { BaseEntity } from "../base-entity";
import { Router } from "aurelia-router";
import { I18N } from "aurelia-i18n";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { StepModel } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { BaseValidationResponse } from "../../../../models/validations/validations.models";
import {
    AddSubtaskRequest,
    VoyageDataSubmissionClientModel, VoyageDataSubmissionStatusEnum,
    VoyageDataSubmissionStepEnum
} from "../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { Reflection } from "../../../../utilities/reflection";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { LookupValue, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { Assignment } from "../../../components/assignment/assignment";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { Correspondence } from "../common-steps/correspondence/correspondence";
import { EntityItem } from "../entity-item";
import { EntityComments } from "../../common/entity-notes/entity-notes";
import { TitleWithCountHelper } from "../../../../helpers/title-with-count-helper";
import { VoyageDataSubmissionService } from "../../../../services/vetting/voyage-data-submission.service";
import { Guid } from "../../../../utilities/guid";
import { VoyageDataSubmissionHistoryTrail } from "./steps/history-trail/voyage-data-submission-history-trail";
import { VoyageDataSubmissionOverview } from "./steps/overview/voyage-data-submission-overview";
import { VoyageDataSubmissionDocuments } from "./steps/documents/voyage-data-submission-documents";
import { VoyageDataSubmissionVoyageRecords } from "./steps/voyage-records/voyage-data-submission-voyage-records";
import { MenuItem } from "../../../components/menu/menu-item";
import { VoyageService } from "../../../../services/voyage/voyage.service";
import { GenericConfirmationDialogModel } from "../../../../models/basics/generic-confirmation-dialog-model";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { CompCorrespondenceModel } from "../../../../models/vessels/vessel.models";

type VoyageDataSubmissionSource = 'activities';

@autoinject
@transient()
export class VoyageDataSubmission extends BaseEntity<VoyageDataSubmissionClientModel> {

    public shouldRemoveCacheOnDetached: boolean = true;
    public readonly: boolean = true;
    public canViewVoyageDataSubmissionDocuments: boolean;
    public canSubmitVoyageData: boolean;
    public voyageDataSubmissionStatuses: LookupValue[];
    public canChangeVoyageDataSubmissionStatus: boolean;
    public canAssignVoyageDataSubmission: boolean;
    public canComputeVoyageEmissions: boolean;
    public canForceVoyageDataSubmissionReporting: boolean;
    public canViewVoyageDataSubmissionCorrespondence: boolean;
    public canSubmitCarbonAccounting: boolean;
    public canAccessVoyageDataRecords: boolean;
    public entityItem: EntityItem;
    public correspondenceSubtaskId: number;
    public assignment: Assignment;
    public canGenerateExcelFiles: boolean;
    public canDeleteVoyageData: boolean;

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmission>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const source: VoyageDataSubmissionSource = this._params.source;

        switch (source) {
            case 'activities':
                return `${this.i18N.tr('tasks-text')}`;
            default:
                {
                    const prevInstruction = this.router.currentInstruction.previousInstruction;
                    return prevInstruction
                        ? prevInstruction.config.title
                        : this.i18N.tr('back-text');
                }
        }
    }

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmission>(v => v.item), Reflection.getPropertyPath<VoyageDataSubmission>(v => v.item.voyageDataSubmissionStatusId))
    get isClosed() {
        return this.item && (this.item.voyageDataSubmissionStatusId == VoyageDataSubmissionStatusEnum.ReadyForReporting || this.item.voyageDataSubmissionStatusId == VoyageDataSubmissionStatusEnum.Completed);
    }

    async canDeactivate() {
        const rfis = await this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(`RFI-VDS-${this.item.voyageDataSubmissionId}`, CacheLocation.SessionStorage, false);
        if (rfis && !this.isClosed) {
            const result = await new Promise<boolean>((resolve) => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AreYouSureExitDialog, {
                    yes: () => {
                        resolve(true);
                    },
                    no: () => {
                        resolve(false);
                    }
                },
                    true, false, false, 'right');

            });
            return result;
        }
        else if (rfis && this.isClosed) {
            await this.cacheClient.remove(`RFI-VDS-${this.item.voyageDataSubmissionId}`, CacheLocation.SessionStorage, false);
        }

        return true;
    }

    constructor(protected router: Router,
        protected bindingEngine: BindingEngine, private dialogClientService: DialogClientService,
        protected i18N: I18N, private userClaimsService: UserClaimsService,
        protected notificationService: NotificationClientService,
        protected eventAggregator: EventAggregator,
        protected overview: VoyageDataSubmissionOverview,
        protected documents: VoyageDataSubmissionDocuments,
        protected voyageRecords: VoyageDataSubmissionVoyageRecords,
        protected entityComments: EntityComments,
        protected signalClientService: SignalClientService,
        protected validationsClientService: ValidationsClientService,
        protected userClientService: UserClientService,
        private cacheClient: CacheClientService,
        private voyageDataSubmissionService: VoyageDataSubmissionService,
        private correspondence: Correspondence,
        private historyTrail: VoyageDataSubmissionHistoryTrail,
        lookupClientService: LookupClientService,
        private activeMenuItemsService: ActiveMenuItemsService, private voyageService: VoyageService) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService);
        this.overview.viewCorrespondence = this.viewCorrespondenceBySubtaskId.bind(this);
        this.overview.reloadItemFunc = (async () => {
            this.item = await this.getItem(this.getItemId());
            setTimeout(() => {
                this.refreshSteps();
            });
        }).bind(this);
        this.canSubmitVoyageData = this.userClaimsService.hasPermission(Claims.CanSubmitCarbonAccounting);
        this.canViewVoyageDataSubmissionDocuments = this.userClaimsService.hasPermission(Claims.CanViewCarbonAccountingDocuments);
        this.canChangeVoyageDataSubmissionStatus = this.userClaimsService.hasPermission(Claims.CanChangeCarbonAccountingStatus);
        this.canAssignVoyageDataSubmission = this.userClaimsService.hasPermission(Claims.CanAssignCarbonAccounting);
        this.canViewVoyageDataSubmissionCorrespondence = this.userClaimsService.hasPermission(Claims.CanViewCarbonAccountingCorrespondence);
        this.canSubmitCarbonAccounting = this.userClaimsService.hasPermission(Claims.CanSubmitCarbonAccounting);
        this.canAccessVoyageDataRecords = this.userClaimsService.hasPermission(Claims.CanAccessVoyageDataRecords);
        this.canComputeVoyageEmissions = this.userClaimsService.hasPermission(Claims.CanComputeVoyageEmissions);
        this.canForceVoyageDataSubmissionReporting = this.userClaimsService.hasPermission(Claims.CanForceVoyageDataSubmissionReporting);
        this.canGenerateExcelFiles = this.userClaimsService.hasPermission(Claims.CanGenerateExcelFiles);
        this.canDeleteVoyageData = this.userClaimsService.hasPermission(Claims.CanDeleteCarbonAccountingVoyageData);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VoyageDataSubmissionStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.voyageDataSubmissionStatuses = x.activeValues;
        });
        overview.navigateToDocumentsTabFunc = () => {
            const step = this.steps.filter(x => x.step == VoyageDataSubmissionStepEnum.Documents)[0];
            this.entityItem.currentStep = step;
        }
    }

    async submit() {
        try {
            await this.voyageDataSubmissionService.changeStatus(this.item.voyageDataSubmissionId, VoyageDataSubmissionStatusEnum.Submitted, "", null, [], "", null, [], [], [], null, null, "");
            this.notificationService.show("Voyage Data Submission", "Submitted Successfully", NotificationType.Success);
        } catch {
            this.notificationService.show("Voyage Data Submission", "Failed to submit", NotificationType.Error);
        }
    }

    viewCorrespondenceBySubtaskId(subtaskId: number) {
        this.correspondenceSubtaskId = subtaskId;
        const step = this.steps.filter(x => x.step == VoyageDataSubmissionStepEnum.Correspondence)[0];
        this.entityItem.currentStep = step;
    }

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmission>(v => v.item), Reflection.getPropertyPath<VoyageDataSubmission>(v => v.item.voyageDataSubmissionStatusId))
    public get statusIsFinal(): boolean {
        if (this.item) {
            return this.item.voyageDataSubmissionStatusId == VoyageDataSubmissionStatusEnum.ReadyForReporting ||
                this.item.voyageDataSubmissionStatusId == VoyageDataSubmissionStatusEnum.Completed || this.item.voyageDataSubmissionStatusId == VoyageDataSubmissionStatusEnum.Cancelled;
        }
        return false;
    }

    async assignToMe() {
        if (this.assignment) {
            await this.assignment.assignToMe();
            this.item.voyageDataSubmissionStatusId = VoyageDataSubmissionStatusEnum.InProgress;
            this.item.assignedUserId = this.userClientService.currentUser.id;
            // don't think this is needed
            //this.refreshVoyageDataSubmissionStatus({ entityId: this.item.voyageDataSubmissionId, statusId: VoyageDataSubmissionStatusEnum.InProgress, userName: this.userClientService.currentUser.userName });
        }
    }

    protected onActivation() {
        this.activeMenuItemsService.setActiveFromMenuItem(this.i18N.tr('tasks-text'));
    }

    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Activities;
    }

    protected getSteps(): StepModel[] {
        return [
            {
                lazyLoad: true,
                title: "Overview",
                step: VoyageDataSubmissionStepEnum.Overview,
                vm: this.overview,
                show: true
            },
            {
                title: "Voyage Records",
                step: VoyageDataSubmissionStepEnum.VoyageRecords,
                vm: this.voyageRecords,
                show: this.canAccessVoyageDataRecords,
                onClick: this.voyageRecords.refreshVoyageSubmissionSummaryData
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Documents", this.item.documentsCount),
                step: VoyageDataSubmissionStepEnum.Documents,
                vm: this.documents,
                show: this.canViewVoyageDataSubmissionDocuments
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Private Notes", this.item.privateNotesCount),
                step: VoyageDataSubmissionStepEnum.PrivateNotes,
                vm: this.entityComments,
                show: this.item.voyageDataSubmissionId > 0 && this.userClaimsService.hasPermission(Claims.CanViewPrivateNotes),
                getModel: () => ({
                    canSubmit: this.userClaimsService.hasPermission(Claims.CanAddPrivateNotes),
                    entityTypeId: this.item.entityType,
                    entityId: this.item.voyageDataSubmissionId,
                    item: this.item
                })
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Correspondences", this.correspondence.correspondenceCount ?? this.item.correspondenceCount),
                step: VoyageDataSubmissionStepEnum.Correspondence,
                vm: this.correspondence,
                getModel: () => {
                    return this.getCorrespondenceModel()
                },
                show: () => this.canViewVoyageDataSubmissionCorrespondence && this.item ? true : false
            },
            {
                lazyLoad: true,
                title: "History Trial",
                step: VoyageDataSubmissionStepEnum.HistoryTrail,
                vm: this.historyTrail,
                show: this.item.voyageDataSubmissionId > 0 && this.userClaimsService.hasPermission(Claims.CanViewCarbonAccountingHistoryTrail)
            }
        ]
    }

    protected getItem(id: number): Promise<VoyageDataSubmissionClientModel> {
        return this.voyageDataSubmissionService.getVoyageDataSubmission(id);
    }

    protected getItemId(): number {
        return this.item ? this.item.voyageDataSubmissionId : null;
    }

    protected saveItem(): Promise<BaseValidationResponse> {
        return null;
    }

    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.VoyageDataSubmission;
    }

    protected getEntityType(): EntityTypes {
        return EntityTypes.VoyageDataSubmission;
    }

    protected initializeItemForCreate(): VoyageDataSubmissionClientModel | Promise<VoyageDataSubmissionClientModel> {
        return {
            assignedUserId: null,
            businessUnitId: null,
            correspondenceCount: 0,
            createdBy: null,
            createdByBusinessUnit: null,
            createdByBusinessUnitId: null,
            createdByEmail: null,
            createdByUserId: null,
            createdDate: null,
            customer: null,
            customerId: null,
            documentsCount: 0,
            privateNotesCount: 0,
            entityType: EntityTypes.VoyageDataSubmission,
            id: 0,
            identifier: Guid.generateGuid(),
            isHosted: false,
            isHostedCustomer: false,
            previousVoyageDataSubmissionStatusId: null,
            voyageDataSubmissionCorrespondenceEmails: [],
            voyageDataSubmissionId: 0,
            voyageDataSubmissionStatus: null,
            voyageDataSubmissionStatusComment: null,
            voyageDataSubmissionStatusCreatedDate: null,
            voyageDataSubmissionStatusHistory: null,
            voyageDataSubmissionStatusId: null,
            lastComputedDate: null,
            ballastLegsIncluded: null
        };
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    attached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.VoyageDataSubmission), this.refreshVoyageDataSubmissionStatus);

        if (this._params && this._params.subtaskId && this._params.subtaskId > 0) {
            this.viewCorrespondenceBySubtaskId(this._params.subtaskId);
            window.history.replaceState(window.history.state, 'Voyage Data Submission', window.location.href.replace(`?subtaskId=${this._params.subtaskId}`, ''));
        }
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.VoyageDataSubmission), this.refreshVoyageDataSubmissionStatus);
    }

    refreshVoyageDataSubmission = async (param) => {
        const id = this.getItemId();
        if (id == param.voyageDataSubmissionId) {
            this.item = await this.getItem(id);
            setTimeout(() => {
                this.refreshSteps();
            });
        }
    };

    refreshVoyageDataSubmissionStatus = (param: EntityStatusChangeModel) => {
        if (this.item.voyageDataSubmissionStatusId == VoyageDataSubmissionStatusEnum.Completed && param.statusId != VoyageDataSubmissionStatusEnum.InProgress) return;
        if (this.item.voyageDataSubmissionId == param.entityId) {

            if (this.assignment && param.statusId && param.statusId != VoyageDataSubmissionStatusEnum.VoyageDataSubmissionReassignment) {
                this.assignment.reload().then(x => this.item.assignedUserId = x.assignedTo);
            }

            const previous = this.item.voyageDataSubmissionStatusHistory[0] ? this.item.voyageDataSubmissionStatusHistory[0].statusId : null;
            if (!param.statusId) return;

            const voyageDataSubmissionStatus = this.voyageDataSubmissionStatuses.filter(x => x.id == param.statusId)[0];

            if (voyageDataSubmissionStatus) {
                this.item.voyageDataSubmissionStatus = voyageDataSubmissionStatus.name;
                this.item.voyageDataSubmissionStatusId = voyageDataSubmissionStatus.id;
                this.item.voyageDataSubmissionStatusHistory.splice(0, 0, {
                    previousStatusId: previous,
                    createdDate: new Date(),
                    status: voyageDataSubmissionStatus.name,
                    statusId: voyageDataSubmissionStatus.id,
                    user: param.userName ? param.userName : "",
                    subtaskTypes: [],
                    comment: param.statusSnippet,
                    fullComment: param.comment
                });
            }
        }
    }

    openVoyageDataSubmissionStatusDialog() {
        if (this.userClaimsService.hasPermission(Claims.CanChangeCarbonAccountingStatus)) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VoyageDataSubmissionStatusDialog,
                {
                    voyageDataSubmissionId: this.item.voyageDataSubmissionId,
                    voyageDataSubmissionStatusId: this.item.voyageDataSubmissionStatusId,
                    refreshVoyageDataSubmission: this.refreshVoyageDataSubmission.bind(this)
                }, true, false, false, 'right');
        }
    }

    @computedFrom("item")
    get menuItems(): MenuItem[] {
        return [{
            click: () => this.computeEmissions(),
            isVisible: this.canComputeVoyageEmissions,
            item: {
                refreshicon: true,
                exporticon: false,
                removeIcon: false,
                text: 'Compute Emissions'
            },
            classList: '',
        },
        {
            click: () => this.generateExcelFiles(),
            isVisible: this.canGenerateExcelFiles,
            item: {
                refreshicon: false,
                exporticon: true,
                removeIcon: false,
                text: 'Generate Excel Files'
            },
            classList: '',
        }, {
            click: () => this.computeEmissions(),
            isVisible: this.canForceVoyageDataSubmissionReporting,
            item: {
                refreshicon: true,
                exporticon: false,
                removeIcon: false,
                text: 'Force Reporting'
            },
            classList: '',
        }, {
            click: () => this.deleteDialog(),
            isVisible: this.canDeleteVoyageData,
            item: {
                refreshicon: false,
                exporticon: false,
                removeIcon: true,
                text: 'Delete Voyage Data'
            },
            classList: '',
        },].filter(x => x.isVisible);
    }

    async computeEmissions() {
        return new Promise<void>((resolve, reject) => {
            this.voyageService.computeEmissions(this.item.voyageDataSubmissionId)
                .then(() => {
                    this.voyageRecords.refreshVoyageSubmissionSummaryData();
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    }

    async forceReporting() {
        return new Promise<void>((resolve, reject) => {
            this.voyageService.forceReporting(this.item.voyageDataSubmissionId)
                .then(() => {
                    this.notificationService.show(this.i18N.tr('success-text'), "Reporting Forced Successfully", NotificationType.Success);
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    }

    deleteDialog() {
        if (this.item.assignedUserId == this.userClientService.currentUser.id || this.isClosed) {
            const model = new GenericConfirmationDialogModel();
            model.confirm = this.i18N.tr('confirm-text');
            model.cancel = this.i18N.tr('cancel-text');
            model.messages.push("Are you sure you want to permanently delete parsed voyage data?");
            model.title = this.i18N.tr('confirm-delete-text');
            model.onConfirm = () => this.deleteVoyageData();
            this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
        }
        else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VoyageDataSubmissionAssignmentDialog, {
                name: "Voyage Data Submission",
                submit: this.assignToMe.bind(this),
                callback: this.deleteDialog.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }

    }

    deleteVoyageData() {
        return new Promise<void>((resolve, reject) => {
            this.voyageDataSubmissionService.deleteVoyageData(this.item.voyageDataSubmissionId)
                .then(() => {
                    this.voyageRecords.refresh();
                    this.documents.loadAttachments().then(() => {
                        this.notificationService.show(this.i18N.tr('success-text'), "Voyages Data Deleted Successfully", NotificationType.Success);
                    });
                    resolve();
                })
                .catch(() => {
                    this.notificationService.show(this.i18N.tr('error-text'), "Failed to delete voyages data", NotificationType.Error);
                    reject();
                });
        });
    }

    async generateExcelFiles() {
        try {
            await this.voyageService.generateExcelFiles(this.item.voyageDataSubmissionId);
            this.notificationService.show(this.i18N.tr('success-text'), "Excels Generated Successfully", NotificationType.Success, 5000);
        } catch {
            this.notificationService.show(this.i18N.tr('error-text'), "Failed to generate excels", NotificationType.Error, 5000);
        }
    }

    private getCorrespondenceModel(): CompCorrespondenceModel {
        return {
            initialSubtaskId: this.correspondenceSubtaskId,
            correspondenceEntity: {
                entityId: this.item.voyageDataSubmissionId,
                entityTypeId: this.item.entityType,
                isInconsistentState: false,
                requestedBy: null,
                liaseWithShipManagers: null,
                assignment: {
                    assignedTo: this.item.assignedUserId,
                    assignedToUserName: this.item.assignedTo,
                    assignToMe: this.assignToMe.bind(this),
                    isAssignedToMe: () => this.item.assignedUserId == this.userClientService.currentUser.id,
                }
            },
            name: "Carbon Accounting",
            showAddRFI: this.item && this.userClaimsService.hasPermission(Claims.CanAddCarbonAccountingRfi),
            hasOutcome: false,
            emails: [],
            newDocumentAdded: () => {
                this.item.documentsCount += 1;
            }
        };
    }
}